import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, fullWidth = false, className, disabled, ...props }, ref) => {
    const baseStyles = 'block w-full rounded-md shadow-sm sm:text-sm transition-colors duration-200';
    const defaultStyles = 'border-gray-300 bg-white focus:border-gray-1000 focus:ring-blue-500';
    const disabledStyles = 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed';
    const errorStyles = 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500';
    const readOnlyStyles = 'border-gray-200 bg-gray-50 text-gray-700';

    return (
      <div className={clsx(fullWidth ? 'w-full' : 'w-auto', className)}>
        {label && (
          <label className={clsx(
            'block text-sm font-medium mb-1',
            disabled ? 'text-gray-500' : 'text-gray-700'
          )}>
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            className={clsx(
              baseStyles,
              disabled ? disabledStyles : defaultStyles,
              error && errorStyles,
              props.readOnly && readOnlyStyles,
              'px-3 py-2'
            )}
            disabled={disabled}
            {...props}
          />
        </div>
        {(error || helperText) && (
          <p className={clsx('mt-1 text-sm', error ? 'text-red-600' : 'text-gray-500')}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
